import React, { useRef } from 'react';

import { Button } from '@components/Button';
import { Container } from '@components/Container';
import { Layout } from '@components/Layout';
import { Pagination } from '@components/Pagination';
import { NotificationCenterItem } from '@components/utilities/NotificationCenter/NotificationCenterItem';
import { useNotification } from '@hooks/queries/useNotification';
import { useSearch } from '@hooks/useSearch';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import * as t from 'io-ts';
import { Link } from 'react-router-dom';
import * as h from 'tyrann-io';

const strings = createStrings({
  ja: {
    title: 'お知らせ',
    noNotification: (
      <>
        ようこそD2D MEMBERSへ！
        <br />
        ただいま、お知らせする情報はありません。
      </>
    ),
  },
});

const searchType = t.type({
  page: h.omittable(h.number().cast()),
});

export const NotificationCenter: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const containerRef = useRef<HTMLDivElement>(null);
  const { search, updateSearch } = useSearch(searchType);
  const { isLoading, error, data, isSuccess } = useNotification({
    page: search.page ?? 0,
  });

  const noticePageCount = 10;
  const renderPagination = () => {
    const pageCount = isSuccess
      ? Math.floor((data!.count + noticePageCount - 1) / noticePageCount)
      : 10;

    if (pageCount === 0) return null;

    return (
      <Pagination
        page={Math.min(Math.max(search.page ?? 0, 0), pageCount - 1)}
        count={pageCount}
        onChange={(page) => {
          updateSearch({ page });
          containerRef.current?.scrollIntoView();
        }}
      />
    );
  };

  return (
    <Layout loading={isLoading} errors={[error]} title={strings.title}>
      {() => (
        <Container
          ref={containerRef}
          classes={{ container: classes.container, inner: classes.inner }}
        >
          <div className={classes.title}>{strings.title}</div>
          <div>
            {data!.results!.map((notice, index) => (
              <NotificationCenterItem key={notice.id} notification={notice} index={index} />
            ))}

            {data?.results.length === 0 && (
              <div className={clsx(classes.middleText, classes.textGray700)}>
                {strings.noNotification}
                <br />
                <br />

                <Link to="/">
                  <Button color="primary" size="small" text="OK" style={{ width: 100 }} />
                </Link>
              </div>
            )}
          </div>
          <div className={classes.paginationContainer}>{renderPagination()}</div>
        </Container>
      )}
    </Layout>
  );
};

const useStyles = makeMuiStyles((theme, common) => ({
  title: {
    ...common.textPrimary,
    ...common.fontWeight600,
    fontSize: 44,
    marginBottom: 40,
  },
  paginationContainer: {
    ...common.flexColumn,
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
  container: {
    backgroundColor: 'white',
  },
  inner: {
    paddingTop: 30,
  },
}));
